import { Alert, Button, Fab, Typography } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { AiOutlineDelete } from "react-icons/ai";
import { GoPlus } from "react-icons/go";
import { GrClear } from "react-icons/gr";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Loader from "../../../../components/Loader";
import {
  setDialsLastSyncDate
} from "../../../../config/redux/slices/dialerSlice";
import {
  selectDialsData,
  selectDialsLastSyncDate,
  selectDialsToSync,
} from "../../../../config/redux/slices/dialerSlice/selectors";
import { fetchAllDials } from "../../../../config/redux/slices/dialerSlice/thunks";
import useCronKeyCheck from "../../../../hooks/useCronKeyCheck";
import { formatDateToString } from "../../../../utils/date";
import AddContactModal from "../AddContactModal";
import ClearDialSyncDialog from "../ClearDialSyncDialog";
import DialDeletionModal from "../DialDeletionModal";
import DialItem from "../DialItem";
import {
  StyledBottomCta,
  StyledDialerDashboard,
  StyledDialerDashboardItems,
} from "./styles";
import { getDialsListForUI } from "./utils";

const DialerDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const dailsData = useSelector(selectDialsData);
  const dialsToSync = useSelector(selectDialsToSync);
  const lastSyncDate = useSelector(selectDialsLastSyncDate);
  const isCronApiSet = useCronKeyCheck();
  
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = React.useState(false);
  const [clearSyncDialogVisible, setClearSyncDialogVisible] =
    React.useState(false);

  const dialsList = useMemo(
    () => getDialsListForUI(dailsData.data, dialsToSync, currentTime),
    [dailsData, dialsToSync, currentTime]
  );
  const isLoading = dailsData.state === "pending";

  useEffect(() => {
    const currentDate = formatDateToString(new Date());
    if (
      ["initial", "failed"].includes(dailsData.state) ||
      lastSyncDate !== currentDate
    ) {
      dispatch(fetchAllDials());
      dispatch(setDialsLastSyncDate(currentDate));
    }
  }, []);

  useEffect(() => {
    const i = setInterval(() => setCurrentTime(Date.now()), 1000);
    return () => clearInterval(i);
  }, []);

  const navigateToUserProfile = () => {
    navigate("/app/profile");
  };

  const onAddClick = () => setAddModalVisible(true);
  const onDeleteClick = () => setDeleteModalVisible(true);
  const onClearSyncClick = () => setClearSyncDialogVisible(true);

  return (
    <StyledDialerDashboard>
      {!isCronApiSet && (
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={navigateToUserProfile}
            >
              Setup
            </Button>
          }
        >
          Dialer cannot function until you complete cron-job api key setup
        </Alert>
      )}
      <Typography variant="h6">Dialer ({dialsList.length})</Typography>
      <StyledBottomCta>
        <Fab
          color="primary"
          aria-label="add"
          onClick={onAddClick}
          disabled={!isCronApiSet}
        >
          <GoPlus size="24px" color="white" />
        </Fab>
        {!!dialsList.length && (
          <Fab
            color="error"
            aria-label="delete"
            onClick={onDeleteClick}
            disabled={!isCronApiSet}
          >
            <AiOutlineDelete size="24px" color="white" />
          </Fab>
        )}
        {!!dialsToSync.length && (
          <Fab
            color="warning"
            aria-label="clear"
            onClick={onClearSyncClick}
            disabled={!isCronApiSet}
          >
            <GrClear size="24px" color="white" />
          </Fab>
        )}
      </StyledBottomCta>

      <AddContactModal
        visible={addModalVisible}
        setVisible={setAddModalVisible}
      />
      <DialDeletionModal
        visible={deleteModalVisible}
        setVisible={setDeleteModalVisible}
      />
      <ClearDialSyncDialog
        visible={clearSyncDialogVisible}
        setVisible={setClearSyncDialogVisible}
      />
      {isLoading ? (
        <Loader hideBg={false} showText="Loading dials" />
      ) : (
        <StyledDialerDashboardItems>
          {dialsList.map((item) => (
            <DialItem dial={item} key={item.key} />
          ))}
        </StyledDialerDashboardItems>
      )}
    </StyledDialerDashboard>
  );
};

export default DialerDashboard;
